/**
 * CardStore维护主子孙关系, 现有业务：
 * 1.孙表tableStore数据由所属所有子表行下的对应孙表数据拼接
 * 展示时按Array.filter(rowStore => rowStore.getValue(PARENT_ROW_KEY) === sStore.currentActiveRow?.getValue(ROW_KEY))过滤
 * 2.子表孙表分页已加载的分页数据贮存，切回该页不重新请求，
 * 展示时按Array.filter(rowStore => rowStore.getValue(PAGE_INDEX) === sPagingStore.pageIndex)过滤，查询条件变化重新加载数据重置;
 * 是否开启分页根据cardStore元数据描述的tableStore是否配置了paging做判断
 * **/

/*
* TODO:
* 1. 设计缺陷：开启子孙分页后，前端校验、交互规则、计算公式无法作用到未加载的分页数据
* 2. 子孙表加载数据hooks支持
* 3. UIStateReaction 编辑保存 -> 浏览态不需要执行
*/

import { makeObservable, observable, reaction, action } from 'mobx'
import { v4 as uuid } from 'uuid'
import { cloneDeep, isEqual } from 'lodash'
import { PageStatusType } from '../pagestore/PageStore'
import ErrDisplay from './ErrDisplay'
import { DataState } from '../tablestore/types'
import buildQueryParams from './buildQueryParams'
import injectRowKey from './injectRowKey'
import mergeStoreData from './mergeStoreData'
import { getLoadedPages } from '../../reactions/voucher/ListOfCardReaction'
import { flattenWithoutChildren } from '../treetablestore/utils'

import {
  PARENT_ROW_KEY,
  ROW_KEY,
  PAGE_INDEX,
  DATA_PRIMARY_KEY,
  IGNORE_UNCHANGED_TABLE_ROW,
  MATCH_BY_INDEX,
  APPEND
} from './constants'

import './index.less'

const defaultMergeStrategy = {
  clearStatus: true,
  dropDeletedRows: true,
  unpersistedRowMerge: MATCH_BY_INDEX
}

type subDescription = {
  alias: string,
  children: subDescription,
  dataSource: string
}

export default class CardStore {
  public dataClean: any
  public formStore: string
  public dataSource: string
  public tableStores: Array<string>
  public rootStore: any
  public uiState: string = 'init'
  public storeType: string = 'CardStore'
  public paramsLoaded: boolean = false
  private merging: boolean = false
  private mergeStrategy: any = defaultMergeStrategy
  constructor(props: any, rootStore: any) {
    const { formStore, tableStores, dataSource } = props
    // 协议dataSource描述
    this.dataSource = dataSource
    this.dataClean = null
    this.formStore = formStore
    this.tableStores = tableStores
    this.rootStore = rootStore
    this.registerReaction()
    // 初始化状态需要依据全局的 pageStore.status
    this.uiState = this.rootStore.pageStore.status
    makeObservable(this, {
      uiState: observable,
      paramsLoaded: observable,
      setUiState: action,
      setValue: action,
      add: action,
      setSubValue: action,
      dispatchData: action
    })
  }

  setUiState(uiState: string) {
    this.uiState = uiState
    // 同步uistate到formStore
    this.rootStore[this.formStore].setUiState(uiState)
    // 同步uistate到子表store
    this.tableStores?.forEach((sDescription) => {
      const { alias: sAlias, children } = sDescription
      this.rootStore[sAlias]?.setUiState?.(uiState)
      // 同步uistate到孙表store
      children?.forEach(gDescription => {
        const { alias: gAlias } = gDescription
        this.rootStore[gAlias]?.setUiState?.(uiState)
      })
    })
  }

  get isBrowse() {
    return this.uiState === PageStatusType.BROWSE;
  }

  get isEdit() {
    return this.uiState === PageStatusType.ADD || this.uiState === PageStatusType.EDIT || this.uiState === PageStatusType.INIT;
  }

  get isAdd() {
    return this.uiState === PageStatusType.ADD
  }

  registerReaction() {
    reaction(
      () => this.rootStore.ready,
      this.rootStoreReadyReaction
    )
  }

  // 页面状态变更reaction
  UIStateReaction = (next: string, prev: string) => {
    if (
      prev === PageStatusType.EDIT &&
      next === PageStatusType.BROWSE
    ) {
      this.dispatchData()
    }
  }

  // rootStore就绪状态变化reaction
  rootStoreReadyReaction = (ready: boolean) => {
    const rootStore = this.rootStore
    if (!ready) return
    reaction(
      () => this.uiState,
      this.UIStateReaction
    )
    this.tableStores?.forEach((sDescription: any) => {
      const { alias: sAlias, children, paging: sPaging, dataSource: sDataSource } = sDescription
      const sStore = rootStore[sAlias]
      sStore.formStoreKey = this.formStore
      sStore.dataSource = sDataSource
      const sPagingStore = rootStore[sPaging]
      // 子表只展示当前激活页数据
      if (sPaging) {
        sStore.multiplePageSelected = true
        sStore.addFilter(row => {
          return row.getValue(PAGE_INDEX) === sPagingStore.pageIndex
        })
      }
      sStore.setHooks('afterAddRow', (row: any) => {
        injectRowKey(row)
        if (
          sPaging &&
          row.getValue(PAGE_INDEX) === undefined
        ) {
          row.setValue(PAGE_INDEX, this.rootStore[sPaging].pageIndex)
        }
      })
      children?.forEach((gDescription: subDescription) => {
        const { alias: gAlias, paging: gPaging, dataSource: gDataSource } = gDescription
        const gStore = this.rootStore[gAlias]
        gStore.formStoreKey = this.formStore
        gStore.dataSource = gDataSource
        if (gPaging) {
          const gPagingStore = this.rootStore[gPaging]
          gPagingStore.multiplePageSelected = true
          gStore.addFilter(row => row.getValue(PAGE_INDEX) === gPagingStore.pageIndex)
        }
        gStore.addFilter((row) => {
          return row.getValue(PARENT_ROW_KEY) === sStore.currentActiveRow?.getValue(ROW_KEY)
        })
        // 孙表新增行注入当前子表行标识并将子表行置为更新
        gStore.setHooks('afterAddRow', (row: any) => {
          const parentRow = sStore.currentActiveRow
          if (!parentRow) return
          const parentSts = parentRow.getValue('_status')
          if (
            gPaging &&
            row.getValue(PAGE_INDEX) === undefined
          ) {
            row.setValue(PAGE_INDEX, this.rootStore[gPaging].pageIndex)
          }
          if (!row.getValue(PARENT_ROW_KEY)) {
            row.setValue(PARENT_ROW_KEY, sStore.currentActiveRow?.getValue(ROW_KEY))
          }
          if (
            !['Update', 'Insert'].includes(parentSts)
            && !row.getValue(DATA_PRIMARY_KEY)
          ) {
            parentRow.setValue('_status', 'Update')
          }
        })
        // 孙表编辑将子表对应行置为更新
        gStore.setHooks('afterRowSetValue', (row: any) => {
          // 由merge出发的孙表值改变不对所属子表行做脏标记
          if (this.merging && this.mergeStrategy?.clearStatus) return
          const parentRowKey = row.getValue(PARENT_ROW_KEY)
          if (!parentRowKey) return
          const parentRow = sStore.getValue().find((row: any) => row.getValue(ROW_KEY) === parentRowKey)
          if (!parentRow) return
          const parentSts = parentRow.getValue('_status')
          if (!['Update', 'Insert'].includes(parentSts)) {
            parentRow.setValue('_status', 'Update')
          }
        })
      })
    })
  }

  validate() {
    let pass = true
    const validateRes: any = { tables: [] }
    const formValidateRes = this.rootStore[this.formStore].validate()
    if (formValidateRes.hasErr) {
      validateRes.form = formValidateRes
      pass = false
    }
    this.tableStores?.forEach((sDescription: any) => {
      const { alias: sAlias, children } = sDescription
      const sStore = this.rootStore[sAlias]
      const sValidateRes = sStore.validate()
      const sInvalid = sValidateRes.filter((res: any, rowIndex: number) => {
        res.rowIndex = rowIndex
        return res?.hasErr
      })
      if (sInvalid.length || (sStore.required && sStore?.noEmptyValidate?.())) {
        pass = false
        validateRes.tables.push({
          caption: sStore.caption,
          requiredErr: sStore.noEmptyValidate(),
          errors: sInvalid
        })
      }
      if (children?.length) {
        const sRowKeys = sStore.dataEntityToJson().map((row: any) => row[ROW_KEY])
        sRowKeys.forEach((sRowKey: any, sIndex: any) => {
          children?.forEach((gDescription: subDescription) => {
          const { alias: gAlias } = gDescription
          const gStore = this.rootStore[gAlias]
          const gValidateRes = gStore.validateByCondition((item: any) => item.getValue(PARENT_ROW_KEY) == sRowKey)
          const gData = gStore.getValueByCondition((item: any) => item.getValue(PARENT_ROW_KEY) == sRowKey)
          const gInvalidGroup: any = {}
          const gInvalid = gValidateRes.filter((res: any, rowIndex: number) => {
            if (res?.hasErr) {
              const gRow = gData[rowIndex]
              const parentRowKey = gData[rowIndex].getValue(PARENT_ROW_KEY)
              const parentIndex = sRowKeys.indexOf(parentRowKey)
              if (!gInvalidGroup[parentIndex]) {
                gInvalidGroup[parentIndex] = {
                  invalid: [],
                  siblings: gData.filter(row => row.getValue(PARENT_ROW_KEY) === parentRowKey)
                }
              }
              gInvalidGroup[parentIndex].invalid.push(res)
              res.rowIndex = gInvalidGroup[parentIndex].siblings.indexOf(gRow)
              return true
            }
            return false
          })
          if (gInvalid.length || (gStore.required && gStore?.noEmptyValidate?.((item: any) => item.getValue(PARENT_ROW_KEY) == sRowKey))) {
            pass = false
            Object.entries(gInvalidGroup).forEach(([sRowIndex, { invalid }]) => {
              validateRes.tables.push({
                caption: `${sStore.caption}${lang.localeUuid('UID:P_CORE-YNF-FE_189530CC05B80003', { num: parseInt(sRowIndex) + 1 }, '【第<%=num%>行】')}${gStore.caption}`, //【第<%=num%>行】
                requiredErr: false,
                errors: invalid
              })
            })
            if (!Object.entries(gInvalidGroup)?.length) {
              validateRes.tables.push({
                caption: `${sStore.caption}${lang.localeUuid('UID:P_CORE-YNF-FE_189530CC05B80003', { num: parseInt(sIndex) + 1 }, '【第<%=num%>行】')}${gStore.caption}`, //非空校验
                requiredErr: gStore.noEmptyValidate((item: any) => item.getValue(PARENT_ROW_KEY) == sRowKey),
                errors: []
              })
            }
          }
          })
        })
      }
    })
    if (!pass) {
      this.rootStore.isPc && this.rootStore.utils.message.danger({
        content: <ErrDisplay cardStore={this} terminalType={this.rootStore.pageStore?.params?.terminalType} errors={validateRes}/>,
        position: 'bottomRight',
        getPopupContainer: this.rootStore.pageStore.params.fragmentId ? document.getElementById(this.rootStore.pageStore.params.fragmentId) : null,
        wrapperStyle: {
          bottom: '55px',
          right: '35px'
        },
        style: { maxHeight: 600, width: 320, overflowY: 'auto' }
      })
      // TODO: 先这样写，后续再优化
      this.rootStore.isMobile && window.TinperM?.Dialog.alert({
        fieldid: 'fieldid-dialog-1',
        title: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B80069', '提交失败', undefined, { returnStr: true }),
        content: <ErrDisplay cardStore={this} terminalType={this.rootStore.pageStore?.params?.terminalType} errors={validateRes}/>,
        confirmText: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6005B8002D', '知道了', undefined, { returnStr: true }),
        closeOnMaskClick: true
      });
    }
    return pass
  }

  setValue (data: any, options = {}) {
    const {
      merge = false,
      mergeStrategy
    } = options
    if (merge) {
      this.mergeData(data, mergeStrategy)
    } else {
      this.dataClean = data
      this.dispatchData()
    }
  }

  // 设置子、孙表数据（塞入的数据处理主子关系后同步到tableStore，同时同步到dataClean以共取消编辑时恢复数据用）
  setSubValue (storeAlias: string, data: any, { concat = false }) {
    const store = this.rootStore[storeAlias]
    let match = null
    // 设置孙表时需要先确定所属子表，parentMatch非空则为孙表
    let parentMatch = null
    let gStores = {}
    let gData = {}
    for (const sDescription of this.tableStores) {
      const { alias: sAlias, children } = sDescription
      if (storeAlias === sAlias) {
        match = sDescription
      }
      if (children) {
        for (const gDescription of children) {
          const { alias: gAlias, dataSource: gDataSource } = gDescription
          if (gAlias === storeAlias) {
            parentMatch = sDescription
            match = gDescription
            break
          }
          const gStore = this.rootStore[gAlias]
          gStores[gAlias] = [gDataSource, gStore]
          gData[gDataSource] = []
        }
      }
      if (match) break
    }
    let parentDataRow
    if (parentMatch) {
      parentDataRow = this.dataClean[parentMatch.dataSource].find(row => row.id === this.rootStore[parentMatch.alias].currentActiveRow?.getValue('id'))
    }
    const nextData = data
    // 不追加则清除子/孙表
    if (!concat) {
      // 清除对应子表
      if (!parentMatch) {
        this.dataClean[match.dataSource] = []
      // 清除对应孙表
      } else {
        parentDataRow[match.dataSource] = []
      }
    }
    data.forEach(row => {
      const rowKey = row.id || uuid()
      row[ROW_KEY] = rowKey
      // 往孙表设值
      if (parentMatch) {
        row[PARENT_ROW_KEY] = this.rootStore[parentMatch.alias].currentActiveRow.getValue(ROW_KEY)
        parentDataRow[match.dataSource].push(row)
      // 往子表设值
      } else {
        const granProps2Del = []
        match.children?.forEach((gDescription) => {
          const { paging: gPaging, dataSource: gDataSource, alias: gAlias } = gDescription
          if (row[gDataSource]?.length) {
            if (gPaging) {
              const gStore = this.rootStore[gAlias]
              if (!gStore.loadedPages) {
                gStore.loadedPages = {}
              }
              if (!gStore.loadedPages[row.id]) {
                gStore.loadedPages[row.id] = []
              }
              gStore.loadedPages[row.id].push({index:1})
            }
            row[gDataSource]?.forEach(gRow => {
              if (gPaging) {
                gRow[PAGE_INDEX] = 1
              }
              gRow[PARENT_ROW_KEY] = row[ROW_KEY]
              gData[gDataSource].push(gRow)
            })
            granProps2Del.push(gDataSource)
          }
        })
        this.dataClean[match.dataSource].push(cloneDeep(row))
        // 摘除孙表数据
        granProps2Del.forEach((propKey: string) => {
          delete row[propKey]
        })
      }
    })

    // 拼接
    if (concat) {
      while (nextData.length) {
        const nextItem = nextData.shift()
        store.addRow(nextItem, true)
      }
      if (!parentMatch) {
        for (const [gDataSource, gStore] of Object.values(gStores)) {
          const nextGData = gData[gDataSource]
          while (nextGData.length) {
            const nextGItem = nextGData.shift()
            gStore.addRow(nextGItem, true)
          }
        }
      }
    // 非拼接--孙表
    } else if (parentMatch) {
      // const parentActiveKey = this.rootStore[parentMatch.alias].currentActiveRow.getValue(ROW_KEY)
      const prevList = store?.data
      let index = prevList.length
      while (index--) {
        const row = prevList[index]
        store.dropRow(row)
        // if (row.getValue(PARENT_ROW_KEY) === parentActiveKey) {
        //   store.dropRow(row)
        // }
      }
      while (nextData.length) {
        store.addRow(nextData.shift(), true)
      }
    // 非拼接--子表
    } else {
      store.setValue(nextData)
      for (const [gDataSource, gStore] of Object.values(gStores)) {
        gStore.setValue(gData[gDataSource])
      }
    }
  }

  getValue ({ dirtyCheck = IGNORE_UNCHANGED_TABLE_ROW} = {}) {
    let rowFilter: Function
    // 脏检查默认只检查子、孙表
    if (dirtyCheck === IGNORE_UNCHANGED_TABLE_ROW) {
      rowFilter = (row: any) => ['Insert', 'Update', 'Delete'].includes(row._status)
    }
    const rootStore = this.rootStore
    // 从formStore收集表头
    const formData = rootStore[this.formStore].getValue()
    const data = {
      ...formData
    }
    // 从tableStore收集子、孙表
    this.tableStores?.forEach((sDescription: any) => {
      const { alias, dataSource, children } = sDescription
      let tableData = flattenWithoutChildren(this.rootStore[alias].dataEntityToJson())
      if (rowFilter) tableData = tableData.filter(rowFilter)
      tableData.forEach((rowData) => {
        if (children?.length) {
          children?.forEach((gDescription) => {
            const { alias: gAlias, dataSource: gDataSource } = gDescription
            const gStore = this.rootStore[gAlias]
            let gData = flattenWithoutChildren(gStore.dataEntityToJson()).filter((gRowData) => {
              let match = false
              if (gRowData[PARENT_ROW_KEY] === rowData[ROW_KEY]) {
                match = true
                delete gRowData[PARENT_ROW_KEY]
              }
              return match
            })
            if (rowFilter) gData = gData.filter(rowFilter)
            rowData[gDataSource] = gData
          })
        }
        delete rowData[ROW_KEY]
      })
      data[dataSource] = tableData
    })
    return data
  }

  // 全量分发数据到成员
  dispatchData () {
    const cardData = cloneDeep(this.dataClean)
    const rootStore = this.rootStore

    // dispatch subTableData
    this.tableStores?.forEach((sDescription: any) => {
      const { alias: sAlias, dataSource: sDataSource, children } = sDescription
      // 子表数据
      const sData = cardData[sDataSource]
      // 子表store
      const sStore = rootStore[sAlias]
      const gData = {}
      const gStores = {}
      children?.forEach((gDescription: subDescription) => {
        const { alias: gAlias, dataSource: gDataSource } = gDescription
        const gStore = rootStore[gAlias]
        gStores[gAlias] = [gDataSource, gStore]
        gData[gDataSource] = []
      })
      if (sData?.length) {
        sData.forEach((sRow) => {
          if (!sRow[ROW_KEY]) {
            sRow[ROW_KEY] = sRow.id || uuid()
          }
          for (const gDataSource in gData) {
            if (!sRow[gDataSource]) continue
            sRow[gDataSource].forEach((gRow) => {
              // 注入子表行key到孙表行
              gRow[PARENT_ROW_KEY] = sRow[ROW_KEY]
              gData[gDataSource].push(gRow)
            })
            // 摘除孙表数据
            delete sRow[gDataSource]
          }
        })
      }
      for (const [gDataSource, gStore] of Object.values(gStores)) {
        gStore.setEntityValue(gData[gDataSource])
      }
      sStore.setEntityValue(sData || [])
      // 摘除子表数据
      delete cardData[sDataSource]
      // 如果有孙表默认选中子表第一行
      if (children?.length && sStore?.size && !sStore?.currentActiveRow) {
        sStore?.setActiveRowKey?.(sStore?.getRow?.(0)?.getValue?.(DATA_PRIMARY_KEY))
      }
    })
    // 分发表头数据到formStore
    rootStore[this.formStore].clear()
    rootStore[this.formStore].setEntityValue(cardData)
  }
  // 合并数据到成员
  // 合并策略：
  // 1、_status为insert的行，按行顺序找对应关系（健壮性有问题）
  // 2、_status为Update的行按ID匹配
  mergeData (data: any, mergeStrategy = defaultMergeStrategy) {
    this.merging = true
    mergeStoreData(this, data, mergeStrategy)
    this.dataClean = this.getValue({ dirtyCheck: false })
    this.merging = false
  }

  add (defaultData: any = {}) {
    // TODO table是否有默认设置n行的业务？
    this.setValue({})
    // 给formStore改变UIState + 前端默认值
    this.rootStore[this.formStore]?.add()
    // 前后端默认值合并
    this.setValue(defaultData, {
      merge: true,
      mergeStrategy: {
        unpersistedRowMerge: APPEND,
        clearStatus: false
      }
    })
    this.setUiState(PageStatusType.ADD)
  }

  openSubPageByRow(options: object = {}) {
    const { pageCode, subFormStoreKey, tableStoreKey, rowIndex, hoverRow } = options
    if (!this.rootStore[subFormStoreKey] || !this.rootStore[tableStoreKey] || !pageCode) return
    // 记录子表的datasource，流程字段权限使用
    this.rootStore[subFormStoreKey].dataSource = this.rootStore[tableStoreKey].dataSource
    this.rootStore[subFormStoreKey].tableStoreKey = tableStoreKey
    // 重置值
    this.rootStore[subFormStoreKey].clear()
    // 获取选中行的数据
    const rowData = this.rootStore[tableStoreKey].getValue()?.[rowIndex.rowIndex] || hoverRow
    // 设置当前行
    this.rootStore[tableStoreKey].setActiveRowKey(rowData?.rowKey)
    // 浏览态点击进入子表详情，再返回点击编辑后，subformStore的状态会一直保持在浏览态
    const tableStoreUiState = this.rootStore[tableStoreKey].uiState
    if (tableStoreUiState) {
      this.rootStore[subFormStoreKey].setUiState(tableStoreUiState)
    }
    // 设置值和index
    if (rowData) {
      const rowStatus = rowData.data._status
      if (rowStatus === DataState.Insert) this.rootStore[subFormStoreKey].add()
      this.rootStore[subFormStoreKey].rowKey = rowData.rowKey
      this.rootStore[subFormStoreKey].setValue(rowData.data)
      // 跳页面
      this.rootStore.pageStore.showPage(pageCode)
    }
  }

  saveSubRow(options: object) {
    const { subFormStoreKey, tableStoreKey, isClose = true } = options
    if (!this.rootStore[subFormStoreKey] || !this.rootStore[tableStoreKey]) return
    // 获取修改后的数据
    const rowKey = this.rootStore[subFormStoreKey].rowKey
    const data = this.rootStore[subFormStoreKey].getValue()
    // 将之前的_status继承回来
    data._status = this.rootStore[tableStoreKey].getRowByKey(rowKey)?.data?._status
    // 更新tableStore
    this.rootStore[tableStoreKey].updateRow(rowKey, data)
    // 关闭当前子页面
    if (isClose) {
      this.rootStore.pageStore.closePage()
    }
  }
  /**
   * 构建detail接口请求参数
   * @param queryRoot 当前查询节点
   * @param queryPaging 是否是查分页
   * @returns
   */
  getQueryParams (queryRoot: object, queryPaging = false) {
    const queryParams = buildQueryParams(this, queryRoot, queryPaging)
    return queryParams
  }

  /**
   * 路径状态是否符合预期
   */
  get isExpectedPathStatus(): Function {
    return (targetStoreKey: string, expectedPathStatus: Array<any>) => {
      const mergeStores = {alias: this.formStore, dataSource: this.dataSource, children: this.tableStores}
      const storePath = this.getTargetStorePath(mergeStores, targetStoreKey)
      const storePathStatus = storePath?.map((item: any) => this.rootStore.get(item)?.uiState)
      return isEqual(storePathStatus, expectedPathStatus)
    }
  }

  get targetSubTableCancelEditVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'edit'])
  }

  get targetSubTableEditVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'browse'])
  }

  get targetSubTableSaveVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'edit'])
  }

  get targetGrandTableSaveVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'browse', 'edit'])
  }

  get targetGrandTableCancelEditVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'browse', 'edit'])
  }

  get targetGrandTableEditVisible(): Function {
    return (targetStoreKey: string) => this.isExpectedPathStatus(targetStoreKey, ['browse', 'browse', 'browse'])
  }
  /**
   * cardStore中是否都是预期状态isAllExpectedStatus("browse")
   */
  get isAllExpectedStatus(): Function {
    return (status: string) => {
      const collectAllAliases = (data: any) => {
        const aliases: any = [];
        const recurse = (item: any) => {
          if (item.alias) {
            aliases.push(item.alias);
          }
          if (item.children) {
            item.children.forEach((child: any) => recurse(child));
          }
        };
        data.forEach((item: any) => recurse(item));
        return aliases;
      };
      const mergeStores = [{ alias: this.formStore, dataSource: this.dataSource, children: this.tableStores }]
      const mergeStoreStatus = collectAllAliases(mergeStores)
      return mergeStoreStatus.every((item: string) => this.rootStore.get(item)?.uiState == status)
    }
  }

  /**
   * 获取目标表格的路径
   * @param data 
   * @param targetStoreKey 
   * @param path 
   * @returns 
   */
  getTargetStorePath(data: any, targetStoreKey: any, path:any = []): Array<any> {
    // 如果当前对象的alias匹配目标值，返回当前路径
    if (data.alias === targetStoreKey) {
        return path.concat(data.alias);
    }
    // 遍历子项
    if (data.children) {
        for (let child of data.children) {
            // 递归调用函数，寻找子项中的匹配项
            const foundPath = this.getTargetStorePath(child, targetStoreKey, path.concat(data.alias));
            if (foundPath.length > 0) {
                return foundPath; // 如果找到，返回路径
            }
        }
    }
    // 如果没有找到匹配项，返回空数组
    return [];
  }

  updateLoadedPages(tableStores: any) {
    for (const tableStoreInfo of tableStores) {
      const { children = [] } = tableStoreInfo
      const loadInfo = getLoadedPages(this, tableStoreInfo)
      loadInfo && loadInfo?.forEach((element: any) => { element.reload = true });
      if (children?.length > 0) {
        this.updateLoadedPages(children)
      }
    }
  }
}
